/**
 * EducationModal — create or edit a single education entry.
 *
 * Fields:
 *   - Institution name
 *   - Degree and Major (free-form text, e.g. "B.S. Computer Science")
 *   - Graduation Date (YYYY-MM-DD); null when "In Progress" is checked
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { educationApi } from '@/lib/api';
import type { Education } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

// ── Form shape ─────────────────────────────────────────────────────────────────

interface FormValues {
  institution: string;
  degreeAndMajor: string;
  graduationDate: string;   // YYYY-MM-DD or empty string
  inProgress: boolean;
}

const EMPTY: FormValues = {
  institution: '',
  degreeAndMajor: '',
  graduationDate: '',
  inProgress: false,
};

// ── Props ──────────────────────────────────────────────────────────────────────

interface EducationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** If supplied, the modal is in edit mode. */
  education?: Education;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function EducationModal({ open, onOpenChange, education }: EducationModalProps) {
  const queryClient = useQueryClient();
  const isEdit = !!education;

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } =
    useForm<FormValues>({ defaultValues: EMPTY });

  const inProgress = watch('inProgress');

  // ── Sync form when modal opens / education changes ─────────────────────────
  useEffect(() => {
    if (open) {
      if (education) {
        reset({
          institution: education.institution,
          degreeAndMajor: education.degreeAndMajor,
          graduationDate: education.graduationDate ?? '',
          inProgress: !education.graduationDate,
        });
      } else {
        reset(EMPTY);
      }
    }
  }, [open, education, reset]);

  // ── Mutations ──────────────────────────────────────────────────────────────

  const createMut = useMutation({
    mutationFn: educationApi.create,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['education'] });
      onOpenChange(false);
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof educationApi.update>[1] }) =>
      educationApi.update(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['education'] });
      onOpenChange(false);
    },
  });

  const isPending = createMut.isPending || updateMut.isPending;
  const error = createMut.error ?? updateMut.error;

  // ── Submit ─────────────────────────────────────────────────────────────────

  const onSubmit = handleSubmit((values) => {
    const graduationDate = values.inProgress ? null : (values.graduationDate || null);

    if (isEdit) {
      updateMut.mutate({
        id: education!.id,
        data: {
          institution: values.institution.trim(),
          degreeAndMajor: values.degreeAndMajor.trim(),
          graduationDate,
        },
      });
    } else {
      createMut.mutate({
        institution: values.institution.trim(),
        degreeAndMajor: values.degreeAndMajor.trim(),
        graduationDate,
      });
    }
  });

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit education' : 'Add education'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4 pt-1">
          {/* Institution */}
          <div className="space-y-1.5">
            <Label htmlFor="edu-institution">Institution</Label>
            <Input
              id="edu-institution"
              placeholder="e.g. Massachusetts Institute of Technology"
              {...register('institution', { required: 'Institution is required' })}
            />
            {errors.institution && (
              <p className="text-xs text-destructive">{errors.institution.message}</p>
            )}
          </div>

          {/* Degree and Major */}
          <div className="space-y-1.5">
            <Label htmlFor="edu-degree">Degree and Major</Label>
            <Input
              id="edu-degree"
              placeholder="e.g. B.S. Computer Science"
              {...register('degreeAndMajor', { required: 'Degree and major is required' })}
            />
            {errors.degreeAndMajor && (
              <p className="text-xs text-destructive">{errors.degreeAndMajor.message}</p>
            )}
          </div>

          {/* Graduation Date / In Progress */}
          <div className="space-y-1.5">
            <Label htmlFor="edu-grad-date">Graduation Date</Label>
            <Input
              id="edu-grad-date"
              type="date"
              disabled={inProgress}
              className={inProgress ? 'opacity-40' : ''}
              {...register('graduationDate')}
            />
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border border-input accent-primary"
                checked={inProgress}
                onChange={(e) => {
                  setValue('inProgress', e.target.checked);
                  if (e.target.checked) setValue('graduationDate', '');
                }}
              />
              In progress / date unknown
            </label>
          </div>

          {error && (
            <p className="text-sm text-destructive">
              {error instanceof Error ? error.message : 'Something went wrong.'}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add education'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
